import { watch, nextTick } from "vue";
import { useTheme } from "vuetify";
import { useAppSettings } from "./useAppSettings";
import type { SiteSettingsUI } from "@cc-consulting-nv/ccsdk";

/**
 * Maps API color settings to Vuetify theme colors
 * API returns camelCase keys (e.g., primaryColorLight, backgroundColorDark)
 */
const COLOR_MAPPING = {
  light: {
    // Primary
    primary: "primaryColorLight",
    "on-primary": "onPrimaryLight",
    "primary-accent": "primaryAccentLight",

    // Secondary
    secondary: "secondaryColorLight",
    "on-secondary": "onSecondaryLight",
    "secondary-accent": "secondaryAccentLight",

    // Tertiary
    tertiary: "tertiaryColorLight",
    "on-tertiary": "onTertiaryLight",
    "tertiary-accent": "tertiaryAccentLight",
    accent: "tertiaryColorLight",

    // Semantic colors
    error: "errorColorLight",
    "on-error": "onError",
    warning: "warningColor",
    "on-warning": "onWarning",
    info: "infoColor",
    "on-info": "onInfo",
    success: "successColor",
    "on-success": "onSuccess",

    // Background & Surface
    background: "backgroundColorLight",
    surface: "backgroundColorLight",
    "surface-variant": "surfaceVariantLight",
    "surface-bright": "backgroundSecondaryLight",

    // On-surface
    "on-surface": "onSurfaceLight",
    "on-surface-variant": "outlineLight",
    "on-background": "onBackgroundLight",

    // Outline
    outline: "outlineLight",
    "outline-variant": "outlineVariantLight",

    // Inverse
    "inverse-surface": "inverseSurfaceLight",
    "inverse-on-surface": "onInverseSurfaceLight",
  },
  dark: {
    // Primary
    primary: "primaryColorDark",
    "on-primary": "onPrimaryDark",
    "primary-accent": "primaryAccentDark",

    // Secondary
    secondary: "secondaryColorDark",
    "on-secondary": "onSecondaryDark",
    "secondary-accent": "secondaryAccentDark",

    // Tertiary
    tertiary: "tertiaryColorDark",
    "on-tertiary": "onTertiaryDark",
    "tertiary-accent": "tertiaryAccentDark",
    accent: "tertiaryColorDark",

    // Semantic colors
    error: "errorColorDark",
    "on-error": "onError",
    warning: "warningColor",
    "on-warning": "onWarning",
    info: "infoColor",
    "on-info": "onInfo",
    success: "successColor",
    "on-success": "onSuccess",

    // Background & Surface
    background: "backgroundColorDark",
    surface: "backgroundSecondaryDark",
    "surface-variant": "surfaceVariantDark",
    "surface-bright": "disabledBackgroundDark",

    // On-surface
    "on-surface": "onSurfaceDark",
    "on-surface-variant": "outlineDark",
    "on-background": "onBackgroundDark",

    // Outline
    outline: "outlineDark",
    "outline-variant": "outlineVariantDark",

    // Inverse
    "inverse-surface": "inverseSurfaceDark",
    "inverse-on-surface": "onInverseSurfaceDark",
  },
} as const;

type ThemeMode = "light" | "dark";
type ColorMapping = typeof COLOR_MAPPING;

/**
 * Composable to apply API theme colors to Vuetify
 */
export function useThemeColors() {
  const theme = useTheme();
  const { uiSettings, isLoaded } = useAppSettings();

  /**
   * Apply colors from API settings to a specific Vuetify theme
   */
  function applyColorsToTheme(
    themeMode: ThemeMode,
    settings: SiteSettingsUI,
  ): void {
    const mapping = COLOR_MAPPING[themeMode];
    const vuetifyTheme = theme.themes.value[themeMode];

    if (!vuetifyTheme) {
      console.warn(`[useThemeColors] Vuetify theme "${themeMode}" not found`);
      return;
    }

    for (const [vuetifyKey, apiKey] of Object.entries(mapping)) {
      const colorValue = settings[apiKey as keyof SiteSettingsUI];
      if (colorValue && typeof colorValue === "string") {
        if (isValidColor(colorValue)) {
          vuetifyTheme.colors[vuetifyKey] = colorValue;
        }
      }
    }
  }

  /**
   * Apply colors to both light and dark themes
   */
  async function applyAllColors(settings: SiteSettingsUI): Promise<void> {
    applyColorsToTheme("light", settings);
    applyColorsToTheme("dark", settings);

    // Force Vuetify to recognize the theme change by toggling and restoring
    const currentTheme = theme.global.name.value;

    // Trigger a theme refresh by briefly toggling (helps Vuetify recalculate CSS vars)
    const otherTheme = currentTheme === "dark" ? "light" : "dark";
    // @ts-ignore - Vuetify's theme.change() is not in types but works in runtime
    theme.change?.(otherTheme);
    await nextTick();
    // @ts-ignore - Vuetify's theme.change() is not in types but works in runtime
    theme.change?.(currentTheme);
  }

  /**
   * Initialize theme colors - watches for settings to load and applies colors
   */
  function initializeThemeColors(): void {
    // Apply immediately if settings are already loaded
    if (isLoaded.value && Object.keys(uiSettings.value).length > 0) {
      applyAllColors(uiSettings.value);
    }

    // Watch for isLoaded to change to true
    watch(
      isLoaded,
      (loaded) => {
        if (loaded && Object.keys(uiSettings.value).length > 0) {
          applyAllColors(uiSettings.value);
        }
      },
      { immediate: true },
    );

    // Also watch for settings changes (for hot reload or refresh)
    watch(
      () => uiSettings.value,
      (newSettings) => {
        if (newSettings && Object.keys(newSettings).length > 0) {
          applyAllColors(newSettings);
        }
      },
      { deep: true },
    );
  }

  return {
    applyColorsToTheme,
    applyAllColors,
    initializeThemeColors,
  };
}

/**
 * Validate that a string is a valid CSS color
 */
function isValidColor(color: string): boolean {
  // Check for hex colors
  if (/^#([0-9A-Fa-f]{3}){1,2}$/.test(color)) {
    return true;
  }
  // Check for rgb/rgba
  if (/^rgba?\(/.test(color)) {
    return true;
  }
  // Check for hsl/hsla
  if (/^hsla?\(/.test(color)) {
    return true;
  }
  // Check for named colors (basic validation)
  if (/^[a-zA-Z]+$/.test(color)) {
    return true;
  }
  return false;
}

export type UseThemeColorsReturn = ReturnType<typeof useThemeColors>;
